import { Button, Table } from 'antd';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CHANNEL_MEDIA_PLAN_STATUS } from '@skyrise-eng/types';
import { useChannelList } from './hooks/useChannelList';
import { Loader, NoData } from '@/components';
import ResizableHeaderCell from '@/components/ResizableHeaderCell';
import { useResizableColumns } from '@/hooks/useResizableColumns';
import type { ChannelListProps } from './ChannelList.types';
import { APP_SCOPE, NAMESPACE } from '@/constants';
import { ActionBar, StyledContainer } from './ChannelList.styles';
import { useRole } from '@/hooks/useRole';

const renderTotalItemsCount = (total: number) => `Total ${total} items`;

const ChannelList: FC<ChannelListProps> = ({
  mediaPlanId,
  planCurrency,
  agencyDiscount,
  isBooked,
  openChannelForm
}) => {
  const { canCreate } = useRole(APP_SCOPE.MEDIA_PLAN);
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const {
    columns: baseColumns,
    data,
    isLoading,
    limit,
    count,
    page,
    handlePaginationChange
  } = useChannelList({
    mediaPlanId,
    planCurrency,
    agencyDiscount,
    isBooked,
    openChannelForm
  });
  const { columns, scrollX } = useResizableColumns(
    'nrve.mediaPlan.lineItemColumnWidths',
    baseColumns
  );

  const renderAddChannelButton = useCallback(
    () =>
      canCreate ? (
        <Button onClick={() => openChannelForm()} type="primary" size="large">
          {t('addLineItem', { ns: NAMESPACE.MEDIA_PLAN_PAGE })}
        </Button>
      ) : (
        <></>
      ),
    [t, canCreate, openChannelForm]
  );

  if (isLoading) {
    return <Loader fullscreen={false} />;
  }

  if (!count) {
    return (
      <NoData
        title={t('noData', { ns: NAMESPACE.COMMON })}
        {...(canCreate && { subtitle: t('addFirstLineItem') })}
      >
        {renderAddChannelButton()}
      </NoData>
    );
  }

  return (
    <StyledContainer>
      <ActionBar justify="end">{renderAddChannelButton()}</ActionBar>
      <Table
        scroll={{ x: scrollX }}
        components={{ header: { cell: ResizableHeaderCell } }}
        loading={isLoading}
        columns={columns}
        dataSource={data}
        rowClassName={({ status }) => (status === CHANNEL_MEDIA_PLAN_STATUS.DRAFT ? 'draft' : '')}
        pagination={{
          showQuickJumper: true,
          totalBoundaryShowSizeChanger: 10,
          showTotal: renderTotalItemsCount,
          defaultPageSize: limit,
          total: count,
          onChange: handlePaginationChange,
          current: page
        }}
      />
    </StyledContainer>
  );
};

export default ChannelList;
